/**
 * approvals.js — client for the approvals surface (FE Contract v2.0 §7).
 *
 * Pure data/transport + pure decision helpers — no React, no DOM.
 *
 * ── SCOPE (§7, verbatim) ────────────────────────────────────────────────────
 * "Approval state is advisory. Export is never blocked by it — a user can
 *  export a pending or rejected layout. Discourage that in the UI if you wish,
 *  but do not assume the backend will refuse."
 *
 * We therefore DISCOURAGE (a warning next to the export action) but never
 * disable it. `APPROVAL_GATES_EXPORT` below is the single switch if a
 * deployment ever wants the stricter behaviour; leave it false to match the
 * contract.
 *
 * Endpoints:
 *   GET  /approvals/by-job/{job_id}   badge + full history for one job
 *   POST /approvals                   { job_id, note? }                 → 201
 *   POST /approvals/{id}/decide       { decision, comment }             → 200
 *   GET  /approvals?state=submitted   approver queue (org-wide)
 *   GET  /approvals?mine=true         my own submissions
 *   GET  /approvals                   operators: only their own
 */

import { parseErrorDetail } from './exportVector';
import { authFetch } from './auth';
import { API_BASE } from './config';

/**
 * §7 is explicit that export must NOT be gated on approval. The design
 * prototype gated it; the contract overrides. Flip to true ONLY if a
 * deployment deliberately wants to block, and expect the backend to still
 * allow it — this is a UI-side courtesy, not enforcement.
 */
export const APPROVAL_GATES_EXPORT = false;

/** Thrown for any non-2xx /approvals response. */
export class ApprovalsApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'ApprovalsApiError';
    this.status = status;      // 403 | 404 | 409 | 422 | 5xx
    this.code = code || null;  // e.g. 'self_approval_forbidden', 'already_pending'
    this.body = body || null;
    // §8: some 409s carry extra context worth surfacing.
    this.approvalId = body?.detail?.approval_id ?? null;   // already_pending
    this.currentState = body?.detail?.state ?? null;       // already_decided
  }
}

async function readError(resp) {
  let body = null;
  try { body = await resp.json(); } catch { /* non-JSON */ }
  const code = body?.detail?.code || body?.code || null;
  const msg =
    (typeof body?.detail === 'string' && body.detail) ||
    body?.detail?.message ||
    body?.message ||
    parseErrorDetail(body) ||
    `Request failed (${resp.status})`;
  return new ApprovalsApiError(msg, resp.status, code, body);
}

/* ------------------------------------------------------------------ */
/* Pure helpers — the contract's rules, unit-testable                   */
/* ------------------------------------------------------------------ */

export const APPROVAL_STATES = ['submitted', 'approved', 'rejected'];

/** §7.2: only these roles may see the queue / decide. */
export function canApprove(me) {
  if (!me) return false;
  return me.role === 'approver' || me.role === 'admin' || me.is_superuser === true;
}

/**
 * §7.3 four-eyes rule: the submitter may not decide their own submission, even
 * as an admin (403 self_approval_forbidden).
 *
 * `refused` lets a caller latch a real 403 — the contract says some deployments
 * relax the rule, so "treat a 403 as authoritative and disable on first
 * refusal" rather than trusting our local prediction in either direction.
 */
export function canDecide(current, me, refused = false) {
  if (refused) return false;
  if (!current || current.state !== 'submitted') return false;
  if (!canApprove(me)) return false;
  if (!me || current.submitted_by == null) return false;
  return current.submitted_by !== me.id;
}

/**
 * §7.2: a rejection MUST carry a non-blank comment (server returns 422
 * comment_required otherwise). Enforce it in the form so the user never eats a
 * round-trip. An approval's comment is optional.
 *
 * @returns {string|null} an error message, or null when valid.
 */
export function validateDecision({ decision, comment } = {}) {
  if (decision !== 'approved' && decision !== 'rejected') {
    return 'Choose approve or reject.';
  }
  if (decision === 'rejected' && !String(comment ?? '').trim()) {
    return 'A reason is required to reject.';
  }
  return null;
}

/**
 * Derive everything the badge UI needs from a /approvals/by-job payload (§7.1).
 * Pure, so the table in §7.1 can be pinned by tests.
 *
 * @returns {{ state, label, tone, canSubmit, stale, detail }}
 *   state  — 'none' | 'submitted' | 'approved' | 'rejected' | 'unknown'
 *   tone   — 'neutral' | 'pending' | 'ok' | 'danger' | 'warn'
 *   stale  — result_matches === false (§7.1: treat as stale, NOT valid)
 */
export function deriveApprovalBadge(payload) {
  const current = payload?.current ?? null;

  if (!current) {
    return {
      state: 'none', label: 'Not submitted', tone: 'neutral',
      canSubmit: true, stale: false, detail: null,
    };
  }

  // §7.1: an explicit false means the job result no longer matches what was
  // approved. It outranks the state — an "approved" badge on a changed result
  // would be actively misleading. (Strict === false: absent means "not checked".)
  const stale = current.result_matches === false;

  const base = (() => {
    switch (current.state) {
      case 'submitted':
        return { state: 'submitted', label: 'Pending', tone: 'pending', canSubmit: false, detail: current.note || null };
      case 'approved':
        return {
          state: 'approved', label: 'Approved', tone: 'ok', canSubmit: false,
          detail: current.decided_by ? `by ${current.decided_by}` : null,
        };
      case 'rejected':
        return {
          state: 'rejected', label: 'Rejected', tone: 'danger', canSubmit: true,
          // §7.1: the comment IS the reason and is mandatory server-side.
          detail: current.comment || null,
        };
      default:
        // §A.5 forward compatibility: an unknown state must not crash or be
        // silently treated as approved.
        return {
          state: 'unknown', label: current.state ? String(current.state) : 'Unknown',
          tone: 'warn', canSubmit: false, detail: null,
        };
    }
  })();

  if (stale) {
    return {
      ...base, tone: 'warn', stale: true,
      label: `${base.label} — result changed`,
      detail: 'This job’s result no longer matches what was reviewed. Treat it as stale, not valid.',
    };
  }
  return { ...base, stale: false };
}

/**
 * §7.2: only *succeeded* jobs can be submitted (409 job_not_approvable
 * otherwise). Predict it so the control can be hidden rather than failing.
 */
export function isJobApprovable(job) {
  return !!job && job.status === 'succeeded';
}

/** Oldest → newest audit trail (§7.1). Defensive against a missing array. */
export function approvalHistory(payload) {
  return Array.isArray(payload?.history) ? payload.history : [];
}

/* ------------------------------------------------------------------ */
/* Calls                                                                */
/* ------------------------------------------------------------------ */

/**
 * Badge + history for one job (§7.1). Returns a normalised payload so callers
 * can rely on the shape.
 *
 * @throws {ApprovalsApiError} — 404 approval_not_found only for a bad job id;
 *         a job with no submission returns 200 with current:null.
 */
export async function getApprovalForJob({ apiBase = API_BASE, jobId, signal } = {}) {
  const resp = await authFetch(`${apiBase}/approvals/by-job/${encodeURIComponent(jobId)}`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  return {
    job_id: data?.job_id ?? jobId,
    current: data?.current ?? null,
    history: Array.isArray(data?.history) ? data.history : [],
  };
}

/**
 * Submit a succeeded job for approval (§7.2) → 201.
 * @throws {ApprovalsApiError} — 409 job_not_approvable (not succeeded),
 *         409 already_pending (detail.approval_id names the open one).
 */
export async function submitForApproval({ apiBase = API_BASE, jobId, note, signal } = {}) {
  const body = { job_id: jobId };
  if (note != null && String(note).trim()) body.note = String(note).trim();
  const resp = await authFetch(`${apiBase}/approvals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Approve or reject (§7.2) → 200. Validates locally first so a blank rejection
 * reason never costs a round-trip.
 *
 * @throws {ApprovalsApiError} — 403 self_approval_forbidden (§7.3),
 *         409 already_decided (detail.state), 422 comment_required.
 */
export async function decideApproval({ apiBase = API_BASE, approvalId, decision, comment, signal } = {}) {
  const invalid = validateDecision({ decision, comment });
  if (invalid) throw new ApprovalsApiError(invalid, 422, 'comment_required', null);

  const resp = await authFetch(`${apiBase}/approvals/${encodeURIComponent(approvalId)}/decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, comment: String(comment ?? '').trim() }),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * List approvals (§7.2).
 *   { state: 'submitted' } → the approver queue, org-wide
 *   { mine: true }         → my own submissions
 *   {}                     → operators see only their own
 */
export async function listApprovals({ apiBase = API_BASE, state, mine, signal } = {}) {
  const qs = new URLSearchParams();
  if (state) qs.set('state', state);
  if (mine) qs.set('mine', 'true');
  const q = qs.toString() ? `?${qs}` : '';
  const resp = await authFetch(`${apiBase}/approvals${q}`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  // Tolerate either a bare array or a wrapped { items } shape (§A.5).
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
}
